




















































































































































import { mixins } from "vue-class-component";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import DatePicker from "vuejs-datepicker";
import Multiselect from "vue-multiselect";
import { create } from "vue-modal-dialogs";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import FormField from "@/lib/components/FormField.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import ignorePasswordManager from "@/lib/directives/ignore-password-manager.directive";
import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import IdGenerator from "@/lib/services/id-generator";
import dataStore from "@/store";
import PlushieStore from "@/modules/plushie/store";

import HolidayPeriodProductGroupDialog from "../ProductGroup/Dialog.vue";

import HolidayPeriod from "../../holiday-period.model";
import {
  cloneProductGroups,
  copyProductGroups,
  validateGroupDraft,
} from "../../holiday-period-group-configuration.helper";
import {
  getCalendarDateKey,
  isValidCalendarDate,
} from "../../calendar-date.helper";
import ProductGroup from "../../product-group.model";
import HolidayPeriodStore from "../../store";

interface GroupDialogProps {
  editing?: boolean;
  name?: string;
  productIds?: string[];
  unavailableProductIds?: string[];
}

interface GroupDialogResult {
  name: string;
  productIds: string[];
}

@Component({
  components: {
    DatePicker,
    FormErrors,
    FormField,
    LoadingSpinner,
    Multiselect,
    SubmitButton,
  },
  directives: { ignorePasswordManager },
})
export default class HolidayPeriodProductGroupSettingsEditForm extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly periodId!: string;

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  public sourcePeriod: HolidayPeriod | null = null;

  private fHolidayPeriodStore: HolidayPeriodStore;
  private fPlushieStore: PlushieStore;
  private fIsLoading = true;
  private fLoadFailed = false;
  private fCopyMessage = "";
  private fCopySucceeded = false;
  private fIsCopying = false;
  private fCopyFeedbackResetTimeoutId?: number;
  private fDraftGroups: ProductGroup[] = [];

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get loadFailed(): boolean {
    return this.fLoadFailed;
  }

  get copyMessage(): string {
    return this.fCopyMessage;
  }

  get copySucceeded(): boolean {
    return this.fCopySucceeded;
  }

  get isCopying(): boolean {
    return this.fIsCopying;
  }

  get isDisabled(): boolean {
    return this.fIsDisabled || this.disable || this.fIsCopying;
  }

  get draftGroups(): ProductGroup[] {
    return this.fDraftGroups;
  }

  get savedGroups(): ProductGroup[] {
    return this.fHolidayPeriodStore.getProductGroupsByPeriodId(this.periodId);
  }

  get removedGroupCount(): number {
    const ids = new Set(this.draftGroups.map((group) => group.id));
    return this.savedGroups.filter((group) => !ids.has(group.id)).length;
  }

  get hasChanges(): boolean {
    return (
      this.removedGroupCount > 0 ||
      this.draftGroups.some((group) => this.isRowChanged(group))
    );
  }

  public isRowChanged(group: ProductGroup): boolean {
    const saved = this.savedGroups.find((item) => item.id === group.id);
    if (group.isNew || !saved) {
      return true;
    }
    const dateKey = (date?: Date): string | undefined =>
      isValidCalendarDate(date) ? getCalendarDateKey(date) : undefined;
    const products = new Set(group.products);
    const savedProducts = new Set(saved.products);
    return (
      group.name !== saved.name ||
      dateKey(group.internationalDueDate) !==
        dateKey(saved.internationalDueDate) ||
      dateKey(group.domesticDueDate) !== dateKey(saved.domesticDueDate) ||
      products.size !== savedProducts.size ||
      Array.from(products).some((id) => !savedProducts.has(id))
    );
  }

  get sourcePeriodOptions(): HolidayPeriod[] {
    return this.fHolidayPeriodStore.holidayPeriodsList.filter(
      (period) => period.id !== this.periodId
    );
  }

  constructor() {
    super();
    this.fHolidayPeriodStore = getModule(HolidayPeriodStore, dataStore);
    this.fPlushieStore = getModule(PlushieStore, dataStore);
  }

  public getProductNames(group: ProductGroup): string {
    const names: string[] = [];

    for (const id of group.products) {
      const product = this.fPlushieStore.getProductById(id);

      if (product) {
        names.push(product.name);
      }
    }

    return names
      .sort((left, right) =>
        left.toLowerCase().localeCompare(right.toLowerCase())
      )
      .join(", ");
  }

  public async addGroup(): Promise<void> {
    if (this.isDisabled) {
      return;
    }

    const dialog = create<GroupDialogProps, GroupDialogResult | undefined>(
      HolidayPeriodProductGroupDialog
    );
    const result = await dialog({
      unavailableProductIds: this.getAssignedProductIds(),
    });

    if (!result) {
      return;
    }

    this.fDraftGroups.push(
      new ProductGroup(
        this.fIdGenerator.getId(),
        this.periodId,
        result.name,
        undefined,
        undefined,
        result.productIds
      )
    );
    this.fCopyMessage = "";
    this.submitErrors = [];
  }

  public async editGroup(group: ProductGroup): Promise<void> {
    if (this.isDisabled) {
      return;
    }

    const dialog = create<GroupDialogProps, GroupDialogResult | undefined>(
      HolidayPeriodProductGroupDialog
    );
    const result = await dialog({
      editing: true,
      name: group.name,
      productIds: group.products,
      unavailableProductIds: this.getAssignedProductIds(group.id),
    });

    if (!result) {
      return;
    }

    group.name = result.name;
    group.products = result.productIds;
    this.submitErrors = [];
  }

  public removeGroup(group: ProductGroup): void {
    if (this.isDisabled) {
      return;
    }

    this.fDraftGroups = this.fDraftGroups.filter(
      (item) => item.id !== group.id
    );
  }

  public async copySourceConfiguration(): Promise<void> {
    if (!this.sourcePeriod || this.isDisabled || this.fIsCopying) {
      return;
    }

    const sourcePeriodId = this.sourcePeriod.id;
    this.resetCopyFeedback();
    this.fCopyMessage = "";
    this.fIsCopying = true;
    try {
      const groups = await this.fHolidayPeriodStore.loadProductGroupsByPeriodId(
        {
          periodId: sourcePeriodId,
          useCache: false,
        }
      );

      if (this.sourcePeriod?.id !== sourcePeriodId) {
        return;
      }

      if (!groups.length) {
        this.fCopyMessage = "The selected period has no groups to copy.";
        return;
      }

      this.fDraftGroups = copyProductGroups(groups, this.periodId, () =>
        this.fIdGenerator.getId()
      );
      this.sortDraftGroups();
      this.submitErrors = validateGroupDraft(this.fDraftGroups);

      if (this.submitErrors.length) {
        this.fCopyMessage =
          "Copied groups need review before Save. Choose a valid date for any leap-day group.";
      }

      this.fCopySucceeded = true;
      this.fCopyFeedbackResetTimeoutId = window.setTimeout(
        () => this.resetCopyFeedback(),
        1800
      );
    } catch (error) {
      this.submitErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsCopying = false;
    }
  }

  public onCancelClick(): void {
    if (!this.isDisabled) {
      this.$emit("cancel");
    }
  }

  protected async created(): Promise<void> {
    this.$once("hook:beforeDestroy", () => this.resetCopyFeedback());
    this.fIsLoading = true;
    try {
      await this.loadData();
      this.fillDraftGroups();
    } catch (error) {
      this.fLoadFailed = true;
      this.submitErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsLoading = false;
    }
  }

  protected async performSubmit(): Promise<void> {
    if (this.fIsCopying) {
      return;
    }

    if (this.fLoadFailed) {
      throw new Error("Holiday product groups could not be loaded.");
    }

    const errors = validateGroupDraft(this.fDraftGroups);

    if (errors.length) {
      this.submitErrors = errors;
      return;
    }

    await this.fHolidayPeriodStore.saveProductGroupsForPeriod({
      periodId: this.periodId,
      items: this.fDraftGroups,
    });
    this.fillDraftGroups();
    this.$emit("updated");
  }

  private async loadData(): Promise<void> {
    await Promise.all([
      this.fHolidayPeriodStore.loadProductGroupsByPeriodId({
        periodId: this.periodId,
        useCache: false,
      }),
      this.fHolidayPeriodStore.loadHolidayPeriods(),
      this.fPlushieStore.loadProducts(),
    ]);
  }

  private fillDraftGroups(): void {
    this.fDraftGroups = cloneProductGroups(this.savedGroups);
    this.sortDraftGroups();
  }

  private sortDraftGroups(): void {
    const dateOrder = (date?: Date): number =>
      isValidCalendarDate(date) ? date.getTime() : Number.MAX_SAFE_INTEGER;

    this.fDraftGroups.sort(
      (left, right) =>
        dateOrder(left.internationalDueDate) -
          dateOrder(right.internationalDueDate) ||
        dateOrder(left.domesticDueDate) - dateOrder(right.domesticDueDate) ||
        this.getProductNames(left)
          .toLowerCase()
          .localeCompare(this.getProductNames(right).toLowerCase())
    );
  }

  private getAssignedProductIds(exceptId?: string): string[] {
    return this.fDraftGroups
      .filter((group) => group.id !== exceptId)
      .reduce((ids: string[], group) => ids.concat(group.products), []);
  }

  private resetCopyFeedback(): void {
    if (this.fCopyFeedbackResetTimeoutId !== undefined) {
      clearTimeout(this.fCopyFeedbackResetTimeoutId);
      this.fCopyFeedbackResetTimeoutId = undefined;
    }

    this.fCopySucceeded = false;
  }

  @Watch("sourcePeriod")
  private onSourcePeriodChanged(): void {
    this.resetCopyFeedback();
  }
}
